import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

// Owns the three AI-generated note artifacts (diagnosis, medical SOAP,
// psychotherapy note), the panel-open booleans, the auto-fetch on mount,
// and the polling loop that auto-loads notes as they become ready.
//
// Refs mirror the three note state values so the polling interval's
// closure always reads CURRENT values (not the values captured when
// the effect first ran). Without these, each 5s tick would refetch
// already-loaded notes and re-fire "auto-generated" toasts in a loop.
export function useAiNotes({
  sessionId,
  token,
  transcript,
  sessionData,
  setSessionData,
}) {
  const [diagnosis, setDiagnosis] = useState(null);
  const [medicalNote, setMedicalNote] = useState(null);
  const [psychotherapyNote, setPsychotherapyNote] = useState(null);

  const [generatingDiagnosis, setGeneratingDiagnosis] = useState(false);
  const [generatingNote, setGeneratingNote] = useState(false);
  const [generatingPsychNote, setGeneratingPsychNote] = useState(false);

  const [showDiagnosisPanel, setShowDiagnosisPanel] = useState(false);
  const [showNotePanel, setShowNotePanel] = useState(false);
  const [showPsychNotePanel, setShowPsychNotePanel] = useState(false);

  const diagnosisRef = useRef(null);
  const medicalNoteRef = useRef(null);
  const psychotherapyNoteRef = useRef(null);

  useEffect(() => { diagnosisRef.current = diagnosis; }, [diagnosis]);
  useEffect(() => { medicalNoteRef.current = medicalNote; }, [medicalNote]);
  useEffect(() => { psychotherapyNoteRef.current = psychotherapyNote; }, [psychotherapyNote]);

  // Belt-and-suspenders: when all three notes land, force notes_generation_status
  // to 'completed' so the auto-generating banner clears immediately.
  useEffect(() => {
    if (diagnosis && medicalNote && psychotherapyNote) {
      setSessionData((prev) => {
        if (prev?.notes_generation_status === 'completed') return prev;
        return { ...(prev || {}), notes_generation_status: 'completed' };
      });
    }
  }, [diagnosis, medicalNote, psychotherapyNote]);

  // Auto-load any notes that already exist for this session on first load.
  useEffect(() => {
    if (!sessionId || !token) return;
    let cancelled = false;
    (async () => {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [mnRes, pnRes, dxRes] = await Promise.all([
        axios.get(`${API}/sessions/${sessionId}/medical-note`, h).catch(() => null),
        axios.get(`${API}/sessions/${sessionId}/psychotherapy-note`, h).catch(() => null),
        axios.get(`${API}/sessions/${sessionId}/diagnosis`, h).catch(() => null),
      ]);
      if (cancelled) return;
      if (mnRes?.data) { setMedicalNote(mnRes.data); setShowNotePanel(true); }
      if (pnRes?.data) { setPsychotherapyNote(pnRes.data); setShowPsychNotePanel(true); }
      if (dxRes?.data) { setDiagnosis(dxRes.data); setShowDiagnosisPanel(true); }
      if (mnRes?.data && pnRes?.data && dxRes?.data) {
        setSessionData((prev) => prev
          ? { ...prev, notes_generation_status: 'completed' }
          : { notes_generation_status: 'completed' }
        );
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, token]);

  // Polling loop — runs while the server hasn't marked notes_generation_status
  // as completed/skipped/failed, OR while diarization is still processing.
  useEffect(() => {
    if (!transcript) return undefined;

    const shouldPoll =
      transcript.diarization_status === 'processing' ||
      !sessionData?.notes_generation_status ||
      sessionData?.notes_generation_status === 'processing' ||
      sessionData?.notes_generation_status === 'pending';

    if (!shouldPoll) return undefined;

    const interval = setInterval(async () => {
      try {
        const [transcriptRes, notesRes] = await Promise.all([
          axios.get(`${API}/transcripts/session/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/sessions/${sessionId}/notes-status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const ns = notesRes.data;
        if (ns.has_diagnosis && !diagnosisRef.current) {
          try {
            const diagRes = await axios.get(`${API}/sessions/${sessionId}/diagnosis`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setDiagnosis(diagRes.data);
            diagnosisRef.current = diagRes.data;
            setShowDiagnosisPanel(true);
            toast.success('Diagnosis auto-generated');
          } catch {}
        }
        if (ns.has_medical_note && !medicalNoteRef.current) {
          try {
            const mnRes = await axios.get(`${API}/sessions/${sessionId}/medical-note`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setMedicalNote(mnRes.data);
            medicalNoteRef.current = mnRes.data;
            setShowNotePanel(true);
            toast.success('Medical note auto-generated');
          } catch {}
        }
        if (ns.has_psychotherapy_note && !psychotherapyNoteRef.current) {
          try {
            const pnRes = await axios.get(`${API}/sessions/${sessionId}/psychotherapy-note`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setPsychotherapyNote(pnRes.data);
            psychotherapyNoteRef.current = pnRes.data;
            setShowPsychNotePanel(true);
            toast.success('Psychotherapy note auto-generated');
          } catch {}
        }

        const allNotesPresent = ns.has_diagnosis && ns.has_medical_note && ns.has_psychotherapy_note;
        const allNotesLocal = !!diagnosisRef.current && !!medicalNoteRef.current && !!psychotherapyNoteRef.current;
        if (
          ns.notes_generation_status === 'completed' ||
          ns.notes_generation_status === 'skipped' ||
          ns.notes_generation_status === 'failed' ||
          allNotesPresent ||
          allNotesLocal
        ) {
          const finalStatus = (ns.notes_generation_status === 'completed' || allNotesPresent || allNotesLocal)
            ? 'completed'
            : ns.notes_generation_status;
          setSessionData((prev) => ({ ...(prev || {}), notes_generation_status: finalStatus }));
          if (finalStatus === 'completed' && sessionData?.notes_generation_status === 'processing') {
            toast.success('All notes auto-generated!');
          }
          clearInterval(interval);
        }

        // Diarization status sync
        if (transcriptRes.data.diarization_status !== transcript.diarization_status) {
          // The transcript itself is owned by useTranscriptData; the consumer
          // must observe diarization separately. For now we fire a toast on
          // transition and let the polling loop on the transcript hook
          // refresh on its own.
          if (transcriptRes.data.diarized_text && !transcript.diarized_text) {
            toast.success('Speaker diarization complete');
          }
        }
      } catch {}
    }, 5000);

    return () => clearInterval(interval);
  }, [transcript?.diarization_status, sessionData?.notes_generation_status]);

  const handleGenerateDiagnosis = async () => {
    if (!transcript) return;
    setGeneratingDiagnosis(true);
    try {
      const response = await axios.post(
        `${API}/transcripts/${transcript.id}/generate-diagnosis`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiagnosis(response.data);
      setShowDiagnosisPanel(true);
      toast.success('AI Diagnosis generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate diagnosis');
    } finally {
      setGeneratingDiagnosis(false);
    }
  };

  const handleGenerateMedicalNote = async () => {
    if (!transcript) return;
    setGeneratingNote(true);
    try {
      const response = await axios.post(
        `${API}/transcripts/${transcript.id}/generate-medical-note`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMedicalNote(response.data);
      setShowNotePanel(true);
      toast.success('Medical note generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate medical note');
    } finally {
      setGeneratingNote(false);
    }
  };

  const handleGeneratePsychotherapyNote = async () => {
    if (!transcript) return;
    setGeneratingPsychNote(true);
    try {
      const response = await axios.post(
        `${API}/transcripts/${transcript.id}/generate-psychotherapy-note`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPsychotherapyNote(response.data);
      setShowPsychNotePanel(true);
      toast.success('Psychotherapy note generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate psychotherapy note');
    } finally {
      setGeneratingPsychNote(false);
    }
  };

  const handleExportMedicalNote = async (format) => {
    if (!medicalNote) return;
    try {
      const endpoint = format === 'fhir'
        ? `${API}/medical-notes/${medicalNote.id}/export/fhir`
        : format === 'ccd'
          ? `${API}/medical-notes/${medicalNote.id}/export/ccd`
          : `${API}/medical-notes/${medicalNote.id}/export/text`;
      const responseType = format === 'fhir' ? 'json' : 'blob';
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType,
      });

      if (format === 'fhir') {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `medical_note_${medicalNote.id}_fhir.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Medical note exported in HL7 FHIR format');
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const extension = format === 'ccd' ? 'xml' : 'txt';
        link.setAttribute('download', `medical_note_${medicalNote.id}.${extension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Medical note exported in ${format.toUpperCase()} format`);
      }
    } catch (error) {
      toast.error('Failed to export medical note');
    }
  };

  const handleExportPsychotherapyNote = async () => {
    if (!psychotherapyNote) return;
    try {
      const response = await axios.get(
        `${API}/psychotherapy-notes/${psychotherapyNote.id}/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `psychotherapy_note_${psychotherapyNote.id}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Psychotherapy note exported');
    } catch (error) {
      toast.error('Failed to export psychotherapy note');
    }
  };

  return {
    diagnosis, setDiagnosis,
    medicalNote, setMedicalNote,
    psychotherapyNote, setPsychotherapyNote,
    generatingDiagnosis,
    generatingNote,
    generatingPsychNote,
    showDiagnosisPanel, setShowDiagnosisPanel,
    showNotePanel, setShowNotePanel,
    showPsychNotePanel, setShowPsychNotePanel,
    handleGenerateDiagnosis,
    handleGenerateMedicalNote,
    handleGeneratePsychotherapyNote,
    handleExportMedicalNote,
    handleExportPsychotherapyNote,
  };
}
